/**
 * Character Sheet — play view. MERGED only. Players never see Sheet A/B
 * during play; the split is only on the level-up page.
 *
 * First live write path: HP edits ride the WebSocket $default dispatcher.
 * The page fetches the character roster over the HTTP read path on mount,
 * picks the first character (multi-character pickers are out of scope for
 * this slice), then opens a campaign socket and listens for runtime patches.
 *
 * Mutations are optimistic: the local state moves immediately and the
 * pending version is tracked. The server's echoed patch (or rejection)
 * confirms or replaces it. Rejections reconcile the local state to the
 * server version — the simplest correct strategy until the audit log
 * grows a "retry with replay" path.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CharacterBundle,
  DEFAULT_SESSION_ID,
  getCharacters,
} from '../lib/sessionApi';
import { CampaignSocket } from '../lib/campaignSocket';
import { reportFrontendError } from '../lib/errorCapture';
import {
  HpUpdateRequest,
  RuntimeActions,
  RuntimePatchPayload,
  RuntimeRejectedPayload,
  RuntimeServerTypes,
  SlotsUpdateRequest,
  SpellSlotsState,
} from '../lib/runtimeProtocol';

// Sheet doesn't render a map but the WS subscribe needs one to bind the
// connection row's SessionId. Reuse the same default the VTT page uses so
// both pages share a session view.
const DEFAULT_MAP_ID = 'local-dev';

interface SheetState {
  bundle: CharacterBundle;
  // Local mirror of the runtime fields the sheet actually drives. Kept
  // separate from the bundle so optimistic updates don't mutate the
  // server-derived snapshot.
  currentHp: number;
  tempHp: number;
  maxHp: number;
  // Per-sheet expended-slot map. Keys are sheet ids ("sheet-a", "sheet-b").
  // Empty object on a freshly seeded character.
  spellSlots: Record<string, SpellSlotsState>;
  version: number;
}

function readMaxHp(bundle: CharacterBundle): number {
  // Placeholder until the merged ledger derives true max HP. The seed
  // writes a placeholderMaxHp into the runtime.currentHp on first save,
  // so on a freshly seeded character we can use that as max. After any
  // mutation the runtime.currentHp drifts from max — fall back to a
  // sensible derivation when that happens.
  const profile = bundle.profile as
    | { sheetA?: { hitDieTotal?: number }; sheetB?: { hitDieTotal?: number } }
    | null;
  if (profile?.sheetA?.hitDieTotal && profile?.sheetB?.hitDieTotal) {
    return profile.sheetA.hitDieTotal + profile.sheetB.hitDieTotal + 1;
  }
  return Math.max(1, bundle.runtime?.currentHp ?? 1);
}

function bundleToState(bundle: CharacterBundle): SheetState {
  const r = bundle.runtime ?? {};
  // Runtime is typed as Partial<CharacterRuntime> in sessionApi but the seed
  // writes an empty object for a fresh character — coerce defensively.
  const slots = (r as { spellSlots?: Record<string, SpellSlotsState> }).spellSlots ?? {};
  return {
    bundle,
    currentHp:  r.currentHp ?? 0,
    tempHp:     r.tempHp ?? 0,
    maxHp:      readMaxHp(bundle),
    spellSlots: slots,
    version:    r.version ?? 0,
  };
}

export default function Sheet() {
  const [state, setState] = useState<SheetState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const socketRef = useRef<CampaignSocket | null>(null);
  // Tracks the version we expect the next server patch to carry. Pending
  // mutations bump this optimistically; rejections snap it back.
  const pendingVersionRef = useRef<number>(0);

  // Load the roster + open the socket once on mount.
  useEffect(() => {
    let cancelled = false;
    let cs: CampaignSocket | null = null;

    (async () => {
      try {
        const list = await getCharacters(DEFAULT_SESSION_ID);
        if (cancelled) return;
        const first = list.characters[0];
        if (!first) {
          setError('No characters in this session yet.');
          setLoading(false);
          return;
        }
        const initial = bundleToState(first);
        setState(initial);
        pendingVersionRef.current = initial.version;
        setLoading(false);

        cs = await CampaignSocket.connect({
          sessionId: DEFAULT_SESSION_ID,
          mapId: DEFAULT_MAP_ID,
        });
        if (cancelled) {
          cs.close();
          return;
        }
        socketRef.current = cs;

        cs.on<RuntimePatchPayload>(RuntimeServerTypes.RuntimePatch, (payload) => {
          setState((prev) => {
            if (!prev || prev.bundle.id !== payload.characterId) return prev;
            // Only apply patches that move us forward — out-of-order echoes
            // (which shouldn't happen on a single connection but are cheap
            // to guard against) are ignored.
            if (payload.version <= prev.version) return prev;
            pendingVersionRef.current = payload.version;
            return {
              ...prev,
              version:    payload.version,
              currentHp:  payload.currentHp  ?? prev.currentHp,
              tempHp:     payload.tempHp     ?? prev.tempHp,
              spellSlots: payload.spellSlots ?? prev.spellSlots,
            };
          });
          setConflict(null);
        });

        cs.on<RuntimeRejectedPayload>(RuntimeServerTypes.RuntimeRejected, (payload) => {
          setState((prev) => {
            if (!prev || prev.bundle.id !== payload.characterId) return prev;
            pendingVersionRef.current = payload.version;
            return {
              ...prev,
              version:    payload.version,
              currentHp:  payload.currentHp  ?? prev.currentHp,
              tempHp:     payload.tempHp     ?? prev.tempHp,
              spellSlots: payload.spellSlots ?? prev.spellSlots,
            };
          });
          setConflict(`Rejected: ${payload.reason}. Reconciled to v${payload.version}.`);
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to open sheet');
          setLoading(false);
        }
        // Caught fetch/socket failures never reach window.onerror, so the
        // global capture hook can't see them. Push them through manually
        // so the live error logger has visibility.
        reportFrontendError(err, { source: 'page-sheet', sessionId: DEFAULT_SESSION_ID });
      }
    })();

    return () => {
      cancelled = true;
      cs?.close();
      socketRef.current = null;
    };
  }, []);

  // Slot mutations ship the FULL new map. The client computes it locally
  // (apply the intended delta on top of the current snapshot), the server
  // overwrites under a version check, and the echoed patch confirms.
  // See SlotsUpdateRequest for why we don't ship per-slot deltas.
  const sendSlotsUpdate = useCallback(
    (nextSlots: Record<string, SpellSlotsState>, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: SlotsUpdateRequest = {
        action: RuntimeActions.SlotsUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        spellSlots: nextSlots,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? {
              ...prev,
              spellSlots: nextSlots,
              version:    expected + 1,
            }
          : prev,
      );
    },
    [state],
  );

  const sendHpUpdate = useCallback(
    (nextCurrentHp?: number, nextTempHp?: number, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: HpUpdateRequest = {
        action: RuntimeActions.HpUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        currentHp: nextCurrentHp,
        tempHp:    nextTempHp,
        reason,
      };
      cs.send(req);
      // Optimistic local apply + optimistic version bump. The server's echo
      // confirms; a rejection snaps both back.
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? {
              ...prev,
              currentHp: nextCurrentHp ?? prev.currentHp,
              tempHp:    nextTempHp ?? prev.tempHp,
              version:   expected + 1,
            }
          : prev,
      );
    },
    [state],
  );

  if (loading) return <p>Loading sheet…</p>;
  if (error) return <p style={{ color: 'var(--danger, #e06c75)' }}>Error: {error}</p>;
  if (!state) return <p>No character.</p>;

  const { bundle, currentHp, tempHp, maxHp, spellSlots, version } = state;
  const name = (bundle.profile as { name?: string } | null)?.name ?? bundle.id;

  // Damage and heal apply to currentHp first; temp HP is set explicitly via
  // its own field so a player can dial it after a Heroism cast.
  const adjustHp = (delta: number) => {
    const next = Math.max(-maxHp, Math.min(maxHp, currentHp + delta));
    sendHpUpdate(next, undefined, delta < 0 ? 'damage' : 'heal');
  };

  // Apply a slot delta to one sheet's leveled map and ship the full new map.
  // delta=+1 expends a slot, delta=-1 restores one. We clamp expended >= 0
  // here; the upper bound (max slots) lives in the ledger and isn't wired
  // into the play view yet.
  const adjustSlot = (sheetId: string, level: number, delta: number) => {
    const cur = spellSlots[sheetId] ?? { leveled: {}, pactExpended: null };
    const key = String(level);
    const next = Math.max(0, (cur.leveled[key] ?? 0) + delta);
    const nextSheet: SpellSlotsState = {
      ...cur,
      leveled: { ...cur.leveled, [key]: next },
    };
    sendSlotsUpdate({ ...spellSlots, [sheetId]: nextSheet }, delta > 0 ? 'cast' : 'recover');
  };

  const adjustPact = (sheetId: string, delta: number) => {
    const cur = spellSlots[sheetId];
    if (!cur || cur.pactExpended == null) return;
    const next = Math.max(0, cur.pactExpended + delta);
    sendSlotsUpdate(
      { ...spellSlots, [sheetId]: { ...cur, pactExpended: next } },
      delta > 0 ? 'pact-cast' : 'short-rest',
    );
  };

  const slotSheetIds = Object.keys(spellSlots);

  return (
    <div style={{ maxWidth: '600px' }}>
      <h1>{name}</h1>
      <p style={{ color: 'var(--fg-muted)' }}>v{version} · {bundle.id}</p>

      <section style={{ marginTop: '1rem' }}>
        <h2>Hit Points</h2>
        <div className="num" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          <span style={{ color: currentHp <= 0 ? 'var(--danger, #e06c75)' : 'var(--fg)' }}>
            {currentHp}
          </span>
          <span style={{ color: 'var(--fg-muted)' }}> / {maxHp}</span>
          {tempHp > 0 && (
            <span style={{ color: 'var(--accent, #56b6c2)', marginLeft: '0.75rem' }}>
              +{tempHp} temp
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          {[1, 5, 10].map((d) => (
            <button key={`dmg-${d}`} onClick={() => adjustHp(-d)}>
              -{d}
            </button>
          ))}
          {[1, 5, 10].map((d) => (
            <button key={`heal-${d}`} onClick={() => adjustHp(d)}>
              +{d}
            </button>
          ))}
          <button onClick={() => sendHpUpdate(maxHp, 0, 'long rest')}>Full heal</button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <label htmlFor="tempHp" style={{ color: 'var(--fg-muted)' }}>
            Temp HP
          </label>
          <input
            id="tempHp"
            type="number"
            min={0}
            value={tempHp}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (Number.isFinite(v)) sendHpUpdate(undefined, Math.max(0, v));
            }}
            style={{ width: '4rem' }}
          />
        </div>
      </section>

      <section style={{ marginTop: '1.5rem' }}>
        <h2>Spell Slots</h2>
        {slotSheetIds.length === 0 && (
          <p style={{ color: 'var(--fg-muted)' }}>
            No slot data yet. Slots populate after the level-up page seeds them.
          </p>
        )}
        {slotSheetIds.map((sid) => {
          const sheet = spellSlots[sid];
          const levels = Object.keys(sheet.leveled)
            .map((k) => parseInt(k, 10))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b);
          return (
            <div key={sid} style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--fg-muted)', marginBottom: '0.25rem' }}>{sid}</div>
              {levels.length === 0 && (
                <div style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>
                  no leveled slots
                </div>
              )}
              {levels.map((lvl) => (
                <div
                  key={`${sid}-${lvl}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}
                >
                  <span style={{ width: '5rem' }}>Level {lvl}</span>
                  <button onClick={() => adjustSlot(sid, lvl, -1)}>-</button>
                  <span className="num" style={{ minWidth: '2rem', textAlign: 'center' }}>
                    {sheet.leveled[String(lvl)] ?? 0}
                  </span>
                  <button onClick={() => adjustSlot(sid, lvl, +1)}>+</button>
                  <span style={{ color: 'var(--fg-muted)' }}>expended</span>
                </div>
              ))}
              {sheet.pactExpended != null && (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}
                >
                  <span style={{ width: '5rem' }}>Pact</span>
                  <button onClick={() => adjustPact(sid, -1)}>-</button>
                  <span className="num" style={{ minWidth: '2rem', textAlign: 'center' }}>
                    {sheet.pactExpended}
                  </span>
                  <button onClick={() => adjustPact(sid, +1)}>+</button>
                  <span style={{ color: 'var(--fg-muted)' }}>expended</span>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {conflict && (
        <p style={{ color: 'var(--warn, #e5c07b)', marginTop: '1rem' }}>{conflict}</p>
      )}
    </div>
  );
}
